import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { FolderSourceBinding } from '../models/FolderSourceBinding';
import type { FolderRecord } from '../models/FolderRecord';
import type { SourceImportRun } from '../models/SourceImportRun';
import { FOLDER_RECORD_PROJECTION_POLICY_VERSION } from '../../../core/fields/security/folderRecordProtection';
import { folderSourceAuthorityKeys } from '../../../core/data-profiles/folderSourceAuthorityQueryKeys';
import type { SourceAllowanceDescriptor, SourceAllowanceErrorCode } from '../../access/models/SourceAllowance';

export const SOURCE_IMPORT_RUN_POLL_INTERVAL_MS = 2_000;

export const folderSourceBindingKeys = {
  all: ['folder-source-bindings'] as const,
  list: (folderId: string | undefined) =>
    [...folderSourceBindingKeys.all, 'list', folderId ?? ''] as const,
  runsRoot: (folderId: string | undefined) =>
    [...folderSourceBindingKeys.all, 'runs', folderId ?? ''] as const,
  runs: (folderId: string | undefined, bindingId: string | undefined) =>
    [...folderSourceBindingKeys.runsRoot(folderId), bindingId ?? ''] as const,
  recordsRoot: (folderId: string | undefined, bindingId: string | undefined) =>
    [
      ...folderSourceBindingKeys.all,
      'records',
      FOLDER_RECORD_PROJECTION_POLICY_VERSION,
      folderId ?? '',
      bindingId ?? '',
    ] as const,
  records: (
    folderId: string | undefined,
    bindingId: string | undefined,
    params?: {
      profileId?: string;
      limit?: number;
      offset?: number;
      includeTombstoned?: boolean;
      onlyTombstoned?: boolean;
    },
  ) => [
    ...folderSourceBindingKeys.recordsRoot(folderId, bindingId),
    params?.profileId ?? '',
    params?.limit ?? 25,
    params?.offset ?? 0,
    params?.includeTombstoned ? 'with-retired' : 'active',
    params?.onlyTombstoned ? 'retired-only' : 'all-visible',
  ] as const,
  relationshipsRoot: (folderId: string | undefined) =>
    [
      ...folderSourceBindingKeys.all,
      'relationships',
      FOLDER_RECORD_PROJECTION_POLICY_VERSION,
      folderId ?? '',
    ] as const,
  relationships: (
    folderId: string | undefined,
    params?: {
      relationshipType?: string;
      relationshipTypes?: readonly string[];
      limit?: number;
      offset?: number;
      resolveIdentities?: boolean;
    },
  ) => [
    ...folderSourceBindingKeys.relationshipsRoot(folderId),
    params?.relationshipTypes?.join(',') ?? params?.relationshipType ?? '',
    params?.limit ?? 25,
    params?.offset ?? 0,
    params?.resolveIdentities === false ? 'raw' : 'resolved',
  ] as const,
};

interface FolderSourceBindingsResponse {
  bindings: FolderSourceBinding[];
  sourceAllowance?: SourceAllowanceDescriptor;
}

export class SourceAllowanceApiError extends Error {
  constructor(
    message: string,
    readonly code: SourceAllowanceErrorCode,
    readonly status: number,
    readonly retryAfterSeconds: number | null,
  ) {
    super(message);
    this.name = 'SourceAllowanceApiError';
  }
}

interface SourceBindingSyncResponse {
  binding: FolderSourceBinding;
  run: SourceImportRun | null;
  result: {
    fetched: number;
    batches: number;
    completeSnapshot: boolean;
    created: number;
    updated: number;
    unchanged: number;
    unresolved: number;
    tombstoned: number;
    failed: number;
  };
  checkpoint?: {
    hasMore: boolean;
    nextOffset: number | null;
    offset: number;
    pageSize: number;
    batches: number;
  };
  error?: string;
}

interface SourceBindingRunsResponse {
  runs: SourceImportRun[];
}

export function sourceImportRunsRefetchInterval(
  data: SourceBindingRunsResponse | undefined,
): number | false {
  const status = data?.runs[0]?.status;
  return status === 'queued' || status === 'running'
    ? SOURCE_IMPORT_RUN_POLL_INTERVAL_MS
    : false;
}

interface SourceBindingRecordsResponse {
  records: FolderRecord[];
  hasMore: boolean;
  limit: number;
  offset: number;
}

export interface UpdateFolderSourceBindingRequest {
  sourceLabel?: string;
  syncMode?: 'manual' | 'scheduled';
  status?: 'active' | 'paused';
}

export interface SyncFolderSourceBindingRequest {
  pageSize?: number;
  maxBatches?: number;
  offset?: number;
  completeSnapshot?: boolean;
}

/**
 * Folder-scoped durable source bindings created by the ontology-aligned
 * import path. These are the reusable "this source feeds this graph"
 * contracts that sit beside knowledge_sources and process import jobs.
 */
export function useFolderSourceBindings(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const query = useQuery<FolderSourceBindingsResponse>({
    queryKey: folderSourceBindingKeys.list(folderId),
    enabled: Boolean(folderId),
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId ?? '')}/source-bindings`,
      );
      if (!response.ok) {
        throw new Error(`Failed to load source bindings: ${response.status}`);
      }
      return response.json() as Promise<FolderSourceBindingsResponse>;
    },
    staleTime: 30_000,
  });

  return {
    bindings: query.data?.bindings ?? [],
    sourceAllowance: query.data?.sourceAllowance,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useSyncFolderSourceBinding(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<
    SourceBindingSyncResponse,
    Error,
    { bindingId: string; input?: SyncFolderSourceBindingRequest }
  >({
    mutationFn: async ({ bindingId, input }) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-bindings/${encodeURIComponent(bindingId)}/sync`,
        {
          method: 'POST',
          ...(input ? {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          } : {}),
        },
      );
      const body = await response
        .json()
        .catch(() => ({})) as Partial<SourceBindingSyncResponse> & {
          error?: string;
          code?: SourceAllowanceErrorCode;
        };
      if (!response.ok) {
        if (body.code) {
          throw new SourceAllowanceApiError(
            body.error ?? `Failed to sync source binding: ${response.status}`,
            body.code,
            response.status,
            Number(response.headers.get('Retry-After')) || null,
          );
        }
        throw new Error(body.error ?? `Failed to sync source binding: ${response.status}`);
      }
      return body as SourceBindingSyncResponse;
    },
    onSuccess: (result, variables) => {
      if (result.run) {
        queryClient.setQueryData<SourceBindingRunsResponse>(
          folderSourceBindingKeys.runs(folderId, variables.bindingId),
          (current) => ({
            runs: [
              result.run!,
              ...(current?.runs ?? []).filter((run) => run.id !== result.run!.id),
            ],
          }),
        );
      }
      void queryClient.invalidateQueries({ queryKey: folderSourceBindingKeys.list(folderId) });
      void queryClient.invalidateQueries({
        queryKey: folderSourceBindingKeys.runs(folderId, variables.bindingId),
      });
      void queryClient.invalidateQueries({
        queryKey: folderSourceBindingKeys.recordsRoot(folderId, variables.bindingId),
      });
      void queryClient.invalidateQueries({
        queryKey: folderSourceBindingKeys.relationshipsRoot(folderId),
      });
      void queryClient.invalidateQueries({ queryKey: ['folder-records', folderId] });
    },
  });
}

export function useSelectSourceAllowance(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation<
    { sourceAllowance: SourceAllowanceDescriptor },
    Error,
    { bindingId: string }
  >({
    mutationFn: async ({ bindingId }) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-bindings/${encodeURIComponent(bindingId)}/allowance-selection`,
        { method: 'PUT' },
      );
      const body = await response.json().catch(() => ({})) as {
        sourceAllowance?: SourceAllowanceDescriptor;
        error?: string;
        code?: SourceAllowanceErrorCode;
      };
      if (!response.ok || !body.sourceAllowance) {
        if (body.code) {
          throw new SourceAllowanceApiError(
            body.error ?? `Failed to select source: ${response.status}`,
            body.code,
            response.status,
            Number(response.headers.get('Retry-After')) || null,
          );
        }
        throw new Error(body.error ?? `Failed to select source: ${response.status}`);
      }
      return { sourceAllowance: body.sourceAllowance };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderSourceBindingKeys.all });
    },
  });
}

export function useFolderSourceBindingRuns(
  folderId: string | undefined,
  bindingId: string | undefined,
  enabled = true,
) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const handledTerminalRunId = useRef<string | null>(null);

  const query = useQuery<SourceBindingRunsResponse>({
    queryKey: folderSourceBindingKeys.runs(folderId, bindingId),
    enabled: enabled && Boolean(folderId) && Boolean(bindingId),
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId ?? '')}/source-bindings/${encodeURIComponent(bindingId ?? '')}/runs`,
      );
      if (!response.ok) {
        throw new Error(`Failed to load source binding runs: ${response.status}`);
      }
      return response.json() as Promise<SourceBindingRunsResponse>;
    },
    staleTime: 30_000,
    refetchInterval: (current) => sourceImportRunsRefetchInterval(
      current.state.data as SourceBindingRunsResponse | undefined,
    ),
    refetchIntervalInBackground: false,
  });

  const latestRun = query.data?.runs[0];
  useEffect(() => {
    if (!latestRun || latestRun.status === 'queued' || latestRun.status === 'running') {
      handledTerminalRunId.current = null;
      return;
    }
    if (handledTerminalRunId.current === latestRun.id) return;
    handledTerminalRunId.current = latestRun.id;

    void queryClient.invalidateQueries({ queryKey: folderSourceBindingKeys.list(folderId) });
    void queryClient.invalidateQueries({
      queryKey: folderSourceBindingKeys.recordsRoot(folderId, bindingId),
    });
    void queryClient.invalidateQueries({
      queryKey: folderSourceBindingKeys.relationshipsRoot(folderId),
    });
    void queryClient.invalidateQueries({ queryKey: ['folder-records', folderId] });
    void queryClient.invalidateQueries({
      queryKey: folderSourceAuthorityKeys.overview(folderId),
    });
    void queryClient.invalidateQueries({
      predicate: (candidate) => candidate.queryKey[0] === 'atlas'
        && candidate.queryKey.includes(folderId),
    });
  }, [bindingId, folderId, latestRun, queryClient]);

  return query;
}

export function useFolderSourceBindingRecords(
  folderId: string | undefined,
  bindingId: string | undefined,
  options?: {
    profileId?: string;
    limit?: number;
    offset?: number;
    includeTombstoned?: boolean;
    onlyTombstoned?: boolean;
    enabled?: boolean;
  },
) {
  const authenticatedFetch = useAuthenticatedFetch();
  const limit = options?.limit ?? 5;
  const offset = options?.offset ?? 0;
  const profileId = options?.profileId;
  const includeTombstoned = options?.includeTombstoned ?? false;
  const onlyTombstoned = options?.onlyTombstoned ?? false;

  return useQuery<SourceBindingRecordsResponse>({
    queryKey: folderSourceBindingKeys.records(folderId, bindingId, {
      profileId,
      limit,
      offset,
      includeTombstoned,
      onlyTombstoned,
    }),
    enabled: (options?.enabled ?? true) && Boolean(folderId) && Boolean(bindingId),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (profileId) params.set('profileId', profileId);
      if (includeTombstoned) params.set('includeTombstoned', 'true');
      if (onlyTombstoned) params.set('onlyTombstoned', 'true');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId ?? '')}/source-bindings/${encodeURIComponent(bindingId ?? '')}/records?${params}`,
      );
      if (!response.ok) {
        throw new Error(`Failed to load source binding records: ${response.status}`);
      }
      return response.json() as Promise<SourceBindingRecordsResponse>;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useFolderRelationships(
  folderId: string | undefined,
  options?: {
    relationshipType?: string;
    relationshipTypes?: readonly string[];
    limit?: number;
    offset?: number;
    resolveIdentities?: boolean;
    enabled?: boolean;
  },
) {
  const authenticatedFetch = useAuthenticatedFetch();
  const limit = options?.limit ?? 25;
  const offset = options?.offset ?? 0;
  const relationshipType = options?.relationshipType;
  const relationshipTypes = options?.relationshipTypes;
  const resolveIdentities = options?.resolveIdentities ?? true;

  return useQuery<SourceBindingRecordsResponse>({
    queryKey: folderSourceBindingKeys.relationships(folderId, {
      relationshipType,
      relationshipTypes,
      limit,
      offset,
      resolveIdentities,
    }),
    enabled: (options?.enabled ?? true) && Boolean(folderId),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (resolveIdentities) params.set('resolveIdentities', 'true');
      if (relationshipTypes && relationshipTypes.length > 0) {
        params.set('relationshipType', relationshipTypes.join(','));
      } else if (relationshipType) {
        params.set('relationshipType', relationshipType);
      }
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId ?? '')}/relationships?${params}`,
      );
      if (!response.ok) {
        throw new Error(`Failed to load relationship records: ${response.status}`);
      }
      return response.json() as Promise<SourceBindingRecordsResponse>;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateFolderSourceBinding(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<
    { binding: FolderSourceBinding },
    Error,
    { bindingId: string; input: UpdateFolderSourceBindingRequest }
  >({
    mutationFn: async ({ bindingId, input }) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-bindings/${encodeURIComponent(bindingId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        },
      );
      const body = await response
        .json()
        .catch(() => ({})) as { binding?: FolderSourceBinding; error?: string };
      if (!response.ok) {
        throw new Error(body.error ?? `Failed to update source binding: ${response.status}`);
      }
      return body as { binding: FolderSourceBinding };
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: folderSourceBindingKeys.list(folderId) });
      void queryClient.invalidateQueries({
        queryKey: folderSourceBindingKeys.runs(folderId, variables.bindingId),
      });
    },
  });
}

export function useArchiveFolderSourceBinding(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<{ binding: FolderSourceBinding }, Error, { bindingId: string }>({
    mutationFn: async ({ bindingId }) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-bindings/${encodeURIComponent(bindingId)}`,
        { method: 'DELETE' },
      );
      const body = await response
        .json()
        .catch(() => ({})) as { binding?: FolderSourceBinding; error?: string };
      if (!response.ok) {
        throw new Error(body.error ?? `Failed to remove source binding: ${response.status}`);
      }
      return body as { binding: FolderSourceBinding };
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: folderSourceBindingKeys.list(folderId) });
      void queryClient.removeQueries({
        queryKey: folderSourceBindingKeys.runs(folderId, variables.bindingId),
      });
      void queryClient.removeQueries({
        queryKey: folderSourceBindingKeys.recordsRoot(folderId, variables.bindingId),
      });
    },
  });
}
